/**
 * Parâmetros do Pixel da Meta (Facebook/Instagram) montados a partir dos
 * eventos de e-commerce do GA4 (2026-10-25). O GTM lê `meta_params` do
 * dataLayer e repassa ao `fbq("track", ...)` — assim ViewContent, AddToCart,
 * InitiateCheckout e Purchase chegam com valor real, moeda e os códigos do
 * catálogo da Meta.
 *
 * `content_ids` precisa bater com o `g:id` do feed /feeds/meta-catalog.xml,
 * que é o número do produto (pai, nas variações). Por isso cada item pode
 * levar `content_id`; sem ele, cai no `item_id`.
 *
 * Função pura, sem dado pessoal.
 */

export const META_PIXEL_EVENTS: Readonly<Record<string, string>> = {
  view_item: "ViewContent",
  add_to_cart: "AddToCart",
  begin_checkout: "InitiateCheckout",
  purchase: "Purchase",
};

export interface MetaPixelParams {
  content_type: "product";
  content_ids: string[];
  contents: Array<{ id: string; quantity: number; item_price?: number }>;
  value: number;
  currency: string;
  num_items: number;
  /** Só no Purchase: número do pedido (base para deduplicar no futuro). */
  order_id?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function money(value: unknown): number | null {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.round(number * 100) / 100 : null;
}

export function buildMetaPixelParams(event: string, ecommerce: Record<string, unknown>): MetaPixelParams | null {
  if (!META_PIXEL_EVENTS[event]) return null;
  const value = money(ecommerce.value);
  if (value === null) return null;
  const items = Array.isArray(ecommerce.items) ? ecommerce.items.filter(isRecord) : [];
  const contents: MetaPixelParams["contents"] = [];
  for (const item of items) {
    const rawId = item.content_id ?? item.item_id;
    const id = typeof rawId === "number" ? String(rawId) : typeof rawId === "string" ? rawId.trim() : "";
    if (!id) continue;
    const quantity = Math.max(1, Math.trunc(Number(item.quantity)) || 1);
    const price = money(item.price);
    contents.push(price === null ? { id, quantity } : { id, quantity, item_price: price });
  }
  const params: MetaPixelParams = {
    content_type: "product",
    content_ids: [...new Set(contents.map((content) => content.id))],
    contents,
    value,
    currency: typeof ecommerce.currency === "string" && /^[A-Z]{3}$/.test(ecommerce.currency) ? ecommerce.currency : "BRL",
    num_items: contents.reduce((sum, content) => sum + content.quantity, 0),
  };
  if (event === "purchase" && (typeof ecommerce.transaction_id === "string" || typeof ecommerce.transaction_id === "number")) {
    params.order_id = String(ecommerce.transaction_id);
  }
  return params;
}
